import { ChangeEvent, useEffect, useRef } from 'react';
import { useTable } from 'src/components';

/**
 * Page state for APIs paginated with a `skipTo` cursor (the id of the last item of the previous
 * page). The cursor for each visited page is remembered so moving back can reuse it.
 */
export const useCursorPagination = () => {
  const pagesRef = useRef<Record<number, string>>({});
  const rowsPerPageRef = useRef<number>(25);

  const { page, rowsPerPage, onChangeRowsPerPage, setPage } = useTable();

  const pageRef = useRef(page);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  /** Clears the remembered cursors and goes back to the first page. */
  const resetPagination = () => {
    pagesRef.current = {};
    setPage(0);
  };

  /**
   * Resolves the cursor for `nextPage`. Moving forward records the last item of the current page
   * as that page's cursor; moving back reuses the recorded one.
   */
  const getPageCursor = (nextPage: number, currentItems: { id?: string }[]) => {
    const isBack = nextPage < pageRef.current;
    const lastItemId = currentItems[currentItems.length - 1]?.id;

    if (nextPage && !isBack) {
      pagesRef.current[nextPage] = lastItemId as string;
    }

    const skipTo: string | undefined = isBack ? pagesRef.current[nextPage] : lastItemId || '';

    return { skipTo, isBack };
  };

  /** Resets to the first page and returns the newly selected rows per page. */
  const changeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPage(0);
    pagesRef.current = {};
    rowsPerPageRef.current = parseInt(event.target.value) as number;
    onChangeRowsPerPage(event as ChangeEvent<HTMLInputElement>);

    return rowsPerPageRef.current;
  };

  return {
    page,
    setPage,
    rowsPerPage,
    rowsPerPageRef,
    pagesRef,
    resetPagination,
    getPageCursor,
    changeRowsPerPage,
  };
};
